import {
  getProjectActivityLogQueryKeyPrefix,
  getPropertyProjectQueryKey,
  getPropertyProjectsQueryKeyPrefix,
  getPropertyProjectsRealtimeQueryKey,
  getScanNotesQueryKey,
  getScanQueryKey,
  getScansByPropertyProjectIdQueryKeyPrefix,
  getUsersForScanQueryKey,
} from '@kaiizen/kai-pro-api';
import {
  type QueryCache,
  type QueryCacheSubscriptionChangeHandler,
} from '@kaiizen/react-persistent-cache';

import {
  type RealtimeClient,
  type RealtimeSubscription,
} from './RealtimeClient';

const REFETCH_REASON = 'refetch';
const QUERY_KEY_SENTINEL = '__query_key__';
const PROPERTY_PROJECT_QUERY_PREFIX = getPropertyProjectQueryKey('');
const PROPERTY_PROJECTS_QUERY_PREFIX = getPropertyProjectsQueryKeyPrefix();
const PROJECT_ACTIVITY_LOG_QUERY_PREFIX = getProjectActivityLogQueryKeyPrefix(QUERY_KEY_SENTINEL)
  .replace(`${QUERY_KEY_SENTINEL}/`, '');
const SCAN_NOTES_QUERY_PREFIX = getScanNotesQueryKey('');
const SCAN_QUERY_PREFIX = getScanQueryKey(QUERY_KEY_SENTINEL).replace(QUERY_KEY_SENTINEL, '');
const SCANS_BY_PROPERTY_PROJECT_QUERY_PREFIX = getScansByPropertyProjectIdQueryKeyPrefix('');
const USERS_FOR_SCAN_QUERY_PREFIX = getUsersForScanQueryKey('');

/** Returns whether a query displays data affected by a project handoff. */
export const isPropertyProjectRealtimeQueryKey = (key: string) =>
  key.startsWith(PROPERTY_PROJECT_QUERY_PREFIX)
  || key.startsWith(PROPERTY_PROJECTS_QUERY_PREFIX)
  || key.startsWith(PROJECT_ACTIVITY_LOG_QUERY_PREFIX)
  || key.startsWith(SCAN_NOTES_QUERY_PREFIX)
  || key.startsWith(SCAN_QUERY_PREFIX)
  || key.startsWith(SCANS_BY_PROPERTY_PROJECT_QUERY_PREFIX)
  || key.startsWith(USERS_FOR_SCAN_QUERY_PREFIX);

const getPayloadString = (payload: unknown, property: 'propertyProjectId' | 'scanId') => {
  if (!payload || typeof payload !== 'object') return undefined;
  const value = (payload as Record<string, unknown>)[property];
  return typeof value === 'string' ? value : undefined;
};

const refetchPropertyProjectQueries = (queryCache: QueryCache<unknown>, payload: unknown) => {
  const propertyProjectId = getPayloadString(payload, 'propertyProjectId');
  const scanId = getPayloadString(payload, 'scanId');

  if (propertyProjectId) {
    queryCache.refetchAll({
      prefix: getPropertyProjectQueryKey(propertyProjectId),
      reason: REFETCH_REASON,
    });
    queryCache.refetchAll({
      prefix: getProjectActivityLogQueryKeyPrefix(propertyProjectId),
      reason: REFETCH_REASON,
    });
    queryCache.refetchAll({
      prefix: getScansByPropertyProjectIdQueryKeyPrefix(propertyProjectId),
      reason: REFETCH_REASON,
    });
  }
  if (scanId) {
    queryCache.refetchAll({
      prefix: getScanQueryKey(scanId),
      reason: REFETCH_REASON,
    });
    queryCache.refetchAll({
      prefix: getScanNotesQueryKey(scanId),
      reason: REFETCH_REASON,
    });
    queryCache.refetchAll({
      prefix: getUsersForScanQueryKey(scanId),
      reason: REFETCH_REASON,
    });
  }
  queryCache.refetchAll({
    prefix: PROPERTY_PROJECTS_QUERY_PREFIX,
    reason: REFETCH_REASON,
  });
};

/**
 * Keeps one organization-scoped project subscription while turn-related queries
 * are active, so a handoff refreshes the assignment, job lists, and history.
 */
export const createPropertyProjectSubscriptionChangeHandler = (
  realtimeClient: RealtimeClient,
  orgId?: string,
): QueryCacheSubscriptionChangeHandler => {
  const subscribedQueryKeys = new Set<string>();
  let queryCache: QueryCache<unknown> | undefined;
  let subscription: RealtimeSubscription | undefined;

  const subscribe = () => {
    if (subscription || !queryCache || !orgId) return;
    subscription = realtimeClient.subscribe(
      getPropertyProjectsRealtimeQueryKey(orgId),
      'refetch',
      payload => refetchPropertyProjectQueries(queryCache!, payload),
    );
  };

  const unsubscribe = () => {
    if (subscribedQueryKeys.size || !subscription) return;
    subscription.unsubscribe();
    subscription = undefined;
  };

  return (nextQueryCache, key, subscriberCount) => {
    if (!isPropertyProjectRealtimeQueryKey(key)) return;

    queryCache = nextQueryCache;
    if (subscriberCount > 0) subscribedQueryKeys.add(key);
    else subscribedQueryKeys.delete(key);

    subscribe();
    unsubscribe();
  };
};
